import React, { useMemo, useState } from 'react';
import { Alert, Button, Card, Form, Input, InputNumber, Modal, Table, Tag, message } from 'antd';
import { useApolloClient, useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { hasResource } from 'utils/resourceAuth';
import { ResourceCode } from 'constants/resourceCodes';
import {
  FORCE_MERGE_COOPERATION_WAREHOUSES,
  ForceMergeResponse,
  ForceMergeVariables,
  OPS_TOOL_STATION_PREVIEW,
  StationPreview,
  StationPreviewResponse,
  parseIdList,
} from './graphql/opsToolPickupStationGraphql';

/** 预览逐个查询站点，限制单次源站点数，避免一次发起过多请求；超出请分批合并 */
const MAX_SOURCE_IDS = 50;

type PreviewRow = { role: 'target' | 'source'; id: number; station: StationPreview | null };

const STATION_STATUS_DISABLED = 0;
const STATION_STATUS_ACTIVE = 1;
const STATUS_TEXT: Record<number, string> = { 0: '禁用', 1: '启用', 2: '待审核', 3: '草稿' };

/** 预览参数指纹：执行按钮只在「最近一次预览」与当前输入完全一致时可点 */
const fingerprint = (targetId: number | undefined, sourceIds: number[]) =>
  JSON.stringify([targetId, [...sourceIds].sort((a, b) => a - b)]);

const ForceMergeStationPage: React.FC = () => {
  const { t } = useTranslation();
  const canOperate = hasResource([ResourceCode.menu.OPS_TOOL_PICKUP]);
  const client = useApolloClient();
  const [form] = Form.useForm();
  const targetId: number | undefined = Form.useWatch('targetId', form) ?? undefined;
  const sourceText: string | undefined = Form.useWatch('sourceIds', form);
  const parsed = useMemo(() => parseIdList(sourceText), [sourceText]);
  const currentFingerprint = fingerprint(targetId, parsed.ids);

  const [previewRows, setPreviewRows] = useState<PreviewRow[]>([]);
  const [previewFingerprint, setPreviewFingerprint] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmInput, setConfirmInput] = useState('');
  const [result, setResult] = useState<ForceMergeResponse['forceMergeCooperationWarehouses'] | null>(null);

  const [forceMerge, { loading: merging }] = useMutation<ForceMergeResponse, ForceMergeVariables>(
    FORCE_MERGE_COOPERATION_WAREHOUSES,
  );

  const sourceError =
    parsed.invalid.length > 0
      ? `无法识别的内容：${parsed.invalid.slice(0, 10).join(', ')}`
      : targetId != null && parsed.ids.includes(targetId)
        ? '源站点不能包含目标站点'
        : parsed.ids.length > MAX_SOURCE_IDS
          ? `单次最多 ${MAX_SOURCE_IDS} 个源站点，请分批`
          : null;
  const inputReady = canOperate && targetId != null && parsed.ids.length > 0 && !sourceError;

  const target = previewRows.find((row) => row.role === 'target')?.station ?? null;
  const missingIds = previewRows.filter((row) => !row.station).map((row) => row.id);
  const crossHubIds = previewRows
    .filter((row) => row.role === 'source' && row.station && target && row.station.warehouseId !== target.warehouseId)
    .map((row) => row.id);
  // 后端强制合并不校验源站点是否已停用（技术债 K2-5）：已停用的源站多半已被合并过，
  // 常见于合并超时后重试。页面拦住，确需合并走科技侧直调接口
  const disabledSourceIds = previewRows
    .filter((row) => row.role === 'source' && row.station?.status === STATION_STATUS_DISABLED)
    .map((row) => row.id);
  const targetNotActive = target != null && target.status !== STATION_STATUS_ACTIVE;
  const executeReady =
    inputReady &&
    previewFingerprint === currentFingerprint &&
    missingIds.length === 0 &&
    crossHubIds.length === 0 &&
    disabledSourceIds.length === 0;

  const handlePreview = async () => {
    if (!canOperate) {
      message.error('无操作权限');
      return;
    }
    setPreviewLoading(true);
    setResult(null);
    try {
      const ids: Array<Pick<PreviewRow, 'role' | 'id'>> = [
        { role: 'target', id: targetId! },
        ...parsed.ids.map((id) => ({ role: 'source' as const, id })),
      ];
      const rows = await Promise.all(
        ids.map(async ({ role, id }) => {
          const { data } = await client.query<StationPreviewResponse>({
            query: OPS_TOOL_STATION_PREVIEW,
            variables: { id },
            fetchPolicy: 'network-only',
          });
          return { role, id, station: data?.getCooperationWarehouseById ?? null };
        }),
      );
      setPreviewRows(rows);
      setPreviewFingerprint(currentFingerprint);
    } catch (e: any) {
      setPreviewRows([]);
      setPreviewFingerprint(null);
      message.error(e?.message ?? '查询站点失败');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleMerge = async () => {
    try {
      const { data } = await forceMerge({ variables: { targetAddressId: targetId!, sourceAddressIds: parsed.ids } });
      const payload = data?.forceMergeCooperationWarehouses ?? null;
      setResult(payload);
      if (payload?.success) {
        message.success('合并完成');
      } else {
        message.error(payload?.message ?? '合并失败');
      }
    } catch (e: any) {
      // 接口报错 ≠ 没合并：大站点合并可能撞网关超时、响应丢失而后台已完成
      Modal.warning({
        title: '合并结果未知，请勿直接重试',
        content: (
          <div>
            <p>错误信息：{e?.message ?? '未知错误'}</p>
            <p>可能已在后台合并完成。请重新「查询站点」：源站点显示「禁用」即已合并，不要再次执行。</p>
          </div>
        ),
      });
    } finally {
      // 不论成败都要求重新预览：成功后源站已停用，失败后站点状态也可能已变化
      setPreviewFingerprint(null);
      setConfirmOpen(false);
      setConfirmInput('');
    }
  };

  const columns = [
    {
      title: '角色',
      dataIndex: 'role',
      render: (role: PreviewRow['role']) => (role === 'target' ? <Tag color="green">目标（保留）</Tag> : <Tag color="orange">源（停用并入）</Tag>),
    },
    { title: '站点 id', dataIndex: 'id' },
    { title: '名称', render: (_: unknown, row: PreviewRow) => row.station?.name ?? <Tag color="red">查不到</Tag> },
    {
      title: 'Hub',
      render: (_: unknown, row: PreviewRow) =>
        row.station ? (
          <span>
            {row.station.warehouseName ?? row.station.warehouseId}
            {crossHubIds.includes(row.id) && <Tag color="red" className="ml-2">跨 Hub</Tag>}
          </span>
        ) : '-',
    },
    {
      title: '状态',
      render: (_: unknown, row: PreviewRow) => {
        if (!row.station) return '-';
        const text = STATUS_TEXT[row.station.status] ?? row.station.status;
        const abnormal =
          (row.role === 'source' && row.station.status === STATION_STATUS_DISABLED) ||
          (row.role === 'target' && row.station.status !== STATION_STATUS_ACTIVE);
        return abnormal ? <Tag color="red">{text}</Tag> : text;
      },
    },
    {
      title: '建联状态',
      render: (_: unknown, row: PreviewRow) =>
        row.station?.linkStatus ? t(`pickup.addressReviewPool.linkStatus.${row.station.linkStatus}`) : '-',
    },
    {
      title: '地址',
      render: (_: unknown, row: PreviewRow) =>
        row.station
          ? [row.station.addressLine1, row.station.addressLine2, row.station.city, row.station.state, row.station.zipcode]
            .filter(Boolean)
            .join(', ')
          : '-',
    },
  ];

  return (
    <Card title="强制合并站点">
      <Alert
        type="error"
        showIcon
        className="mb-4"
        message="不可逆操作"
        description={
          <div>
            跳过相似度判定与建联状态校验，直接把源站点并入目标站点：源站点停用，地址并入目标站点。跨 Hub 仍会被拦截。
            <br />
            合并后没有回滚入口，只能按审计日志手工反向修数据。执行前必须先查询站点并逐个核对。
          </div>
        }
      />
      <Form form={form} layout="vertical">
        <Form.Item name="targetId" label="目标站点 id（保留）" rules={[{ required: true, message: '请输入目标站点 id' }]}>
          <InputNumber min={1} precision={0} className="!w-60" />
        </Form.Item>
        <Form.Item
          name="sourceIds"
          label="源站点 id（停用并入）"
          validateStatus={sourceError ? 'error' : undefined}
          help={
            sourceError ??
            (parsed.ids.length > 0
              ? `已识别 ${parsed.ids.length} 个源站点${parsed.duplicated > 0 ? `（重复 ${parsed.duplicated} 个已去重）` : ''}`
              : '逗号、空格或换行分隔')
          }
        >
          <Input.TextArea rows={3} />
        </Form.Item>
        <div className="flex gap-2">
          <Button loading={previewLoading} disabled={!inputReady} onClick={handlePreview}>
            查询站点
          </Button>
          <Button type="primary" danger disabled={!executeReady} onClick={() => setConfirmOpen(true)}>
            执行合并
          </Button>
        </div>
      </Form>

      {previewRows.length > 0 && (
        <>
          {(missingIds.length > 0 || crossHubIds.length > 0 || disabledSourceIds.length > 0) && (
            <Alert
              type="error"
              className="mt-4"
              message={[
                missingIds.length > 0 ? `查不到的站点：${missingIds.join(', ')}` : '',
                crossHubIds.length > 0 ? `与目标站点不在同一 Hub：${crossHubIds.join(', ')}` : '',
                disabledSourceIds.length > 0
                  ? `源站点已停用（可能已被合并过，请勿重复合并；确需合并请联系科技侧）：${disabledSourceIds.join(', ')}`
                  : '',
              ].filter(Boolean).join('；')}
            />
          )}
          {targetNotActive && (
            <Alert
              type="warning"
              className="mt-4"
              message={`目标站点当前状态为「${STATUS_TEXT[target!.status] ?? target!.status}」，非启用状态，请确认是否仍要并入`}
            />
          )}
          <Table className="mt-4" size="small" rowKey={(row) => `${row.role}-${row.id}`} columns={columns} dataSource={previewRows} pagination={false} />
        </>
      )}

      {result && (
        <Alert
          className="mt-4"
          type={result.success ? 'success' : 'error'}
          showIcon
          message={result.success ? '合并完成' : '合并失败'}
          description={result.message ?? undefined}
        />
      )}

      <Modal
        open={confirmOpen}
        title="确认强制合并"
        okText="确认合并"
        cancelText="取消"
        okButtonProps={{ danger: true, disabled: confirmInput.trim() !== String(targetId), loading: merging }}
        onOk={handleMerge}
        onCancel={() => {
          setConfirmOpen(false);
          setConfirmInput('');
        }}
      >
        <p>
          将把 {parsed.ids.length} 个源站点（{parsed.ids.join(', ')}）并入目标站点 {targetId}
          {target ? `「${target.name}」` : ''}，源站点会被停用，<b>不可回滚</b>。
        </p>
        <p>请输入目标站点 id 以确认：</p>
        <Input value={confirmInput} onChange={(e) => setConfirmInput(e.target.value)} placeholder={String(targetId ?? '')} />
      </Modal>
    </Card>
  );
};

export default ForceMergeStationPage;
